import { ExternalRedirectHeaderStepperProps } from './props';
import styles from './styles.module.scss';
import { useMemo } from 'react';
import TransactionModalResponseContentStepperStep from './Step';

/**
 * ExternalRedirectHeaderStepper Component
 * Renders a stepper component showing the progress of an external redirect transaction.
 * Creates 3 step indicators that visualize the transaction flow.
 *
 * @component
 * @param {Object} props - Component props
 * @param {TransactionType} props.transaction - The transaction object containing progress and status details
 * @returns {JSX.Element} A container div with 3 step indicators
 *
 * @example
 * const transaction = {
 *   id: "t123",
 *   status: { id: "pending" },
 *   progress: 1, // Current step (0-2)
 * };
 *
 * <ExternalRedirectHeaderStepper transaction={transaction} />
 */
export default function ExternalRedirectHeaderStepper({
  transaction,
}: ExternalRedirectHeaderStepperProps) {
  /**
   * Creates an array of 3 step components using useMemo for performance
   * Each step receives its position index and the transaction data
   * @returns {JSX.Element[]} Array of step components
   */
  const Steps: JSX.Element[] = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, index) => (
        <TransactionModalResponseContentStepperStep
          position={index}
          transaction={transaction}
          key={`transaction-step-${index}`}
        />
      )),
    [transaction],
  );

  return <div className={styles.container}>{Steps}</div>;
}
